import React from 'react';
import {SFEntityID, SFVirtualCheckInDaySchedule} from 'utils/types';
import PropTypes from 'prop-types';
import css from './ServiceProfile.scss';
import HoursSection from './HoursSection';
import ContactSection from './ContactSection';
import LocationSection from './LocationSection';
import OverviewSection from './OverviewSection';
import ServiceHeader from './ServiceHeader';
import ProvidersContainer from './ProvidersContainer';
import WaitingRoom from '../home/services/WaitingRoom';
import LinksSection from './LinksSection';
import StyledButton from 'common/StyledButton';
import ResponsiveView from 'common/ResponsiveView';
import StyledLink from 'common/StyledLink';
import { getTenantConfig } from 'utils/tenant';
import { getVirtualKioskLink } from 'utils/kiosk';
import { defaultScheduleLinkForService } from 'utils/scheduler';
import Icon from 'SF/MaterialIcon';
import VirtualKioskClosedModal from 'common/VirtualKioskClosedModal';

export default class ServiceProfile extends React.Component {

  constructor() {
    super();
    this.state = { displayWaitingRoomClosedDialog: false };
  }

  isValid(prop) {
    return !(prop === null || prop === "" || typeof prop === "undefined");
  }

  get locationSection() {
    const { location } = this.props;
    return this.isValid(location) ? <LocationSection location={location} /> : null;
  }

  isSmartLinkVisible() {
     return ( this.props.displaySmartThinkingLink &&
              this.props.canMakeAppointment &&
              getTenantConfig('SMARTHINKING_LTI_ENABLED')  &&
              (this.props.hasConnectLicense || this.props.canSeeStudentServices))
            || false;
  }

  get contactSection() {
    const { phoneNumber, email, url, id } = this.props;
    const isSmartLinkVisible = this.isSmartLinkVisible();
    return this.isValid(phoneNumber) || this.isValid(email) || this.isValid(url) || isSmartLinkVisible
      ? <ContactSection phoneNumber={phoneNumber}
                        email={email}
                        url={url}
                        isSmartLinkVisible={isSmartLinkVisible}
                        serviceId={id} />
      : null;
  }

  get hoursSection() {
    const { hoursOfOperation, id, virtualKioskSchedule } = this.props;
    return this.isValid(hoursOfOperation) || (virtualKioskSchedule && virtualKioskSchedule.length > 0)
      ? <HoursSection hoursOfOperation={hoursOfOperation}
                      id={id}
                      virtualKioskSchedule={virtualKioskSchedule} />
      : null;
  }

  get kioskButtonText() {
    if (this.props.checkedIntoService)
      return <>
        <Icon className={css['kiosk-icon']} aria-hidden={true} icon="assignment_return" />
        {this.props.virtualCheckInOpen ? "CHECK OUT OF WAITING ROOM" : "CHECK OUT UNAVAILABLE"}
      </>;
    return <>
      <Icon className={css['kiosk-icon']} aria-hidden={true} icon="hourglass_empty" />
      {this.props.virtualCheckInOpen ? "JOIN WAITING ROOM" : "WAITING ROOM CLOSED"}
    </>;
  }

  get virtualCheckInLink() {
    return getTenantConfig('RESPONSIVE_KIOSK_ENABLED') && this.props.virtualCheckInEnabled && this.props.isStudent &&
        <>
          <VirtualKioskClosedModal
              checkedIntoService={this.props.checkedIntoService}
              setDisplayClosed={() => this.setState({ displayWaitingRoomClosedDialog: false })}
              shouldDisplay={this.state.displayWaitingRoomClosedDialog} />
          <div className={css['virtual-check-in-link']}>
            <StyledButton
                className={this.props.checkedIntoService ? css['kiosk-checkout-button'] : css['kiosk-join-button']}
                inverted
                aria-disabled={!this.props.virtualCheckInOpen}
                onClick={() => {
                    this.props.virtualCheckInOpen ?
                        location.assign(getVirtualKioskLink(this.props.kioskId, this.props.id))
                        : this.setState({ displayWaitingRoomClosedDialog: true })
                }}>
              <span className={css['button-label']}>{this.kioskButtonText}</span>
            </StyledButton>
          </div>
        </>
  }

  get overviewSection() {
    const { serviceDescription } = this.props;
    return this.isValid(serviceDescription) ? <OverviewSection serviceDescription={serviceDescription} /> : null;
  }

  get linksSection() {
    const showLinksSection = (
      getTenantConfig('DEEP_LINKS_ENABLED') // Deep links must be enabled
      && !!getTenantConfig('DEEP_LINK_SSO_METHOD') // Needs to have a SSO method set
      && this.props.isStaff // Only staff users can see links section
    );
    const showKioskLink = (
      this.props.virtualCheckInEnabled && getTenantConfig('RESPONSIVE_KIOSK_ENABLED')
    );
    
    return showLinksSection 
      ? (
        <LinksSection 
          id={this.props.id} 
          serviceCalendarEnabled={this.props.serviceCalendarEnabled}
          kioskUuid={showKioskLink ? this.props.kioskUuid : null}
        />
      )
      : null;
  }

  get appointmentSchedulingLink(){
    if(!this.props.onlineSchedulingAvailable) {
      return null;
    }
    return getTenantConfig('MOBILE_APPOINTMENTS_ENABLED') ?
        <StyledLink to={`/service/${this.props.id}/schedule`} text="Schedule Appointment"/>
        : <a href={defaultScheduleLinkForService(this.props.id)}>Schedule</a>;
  }

  render() {
    return (
      <div>
        <ServiceHeader
          name={this.props.name}
          serviceDisplayName={this.props.serviceDisplayName}
          kioskQueueSize={this.props.kioskQueueSize}
          kioskMaxWaitTime={this.props.kioskMaxWaitTime}
          uuid={this.props.uuid}
          kioskId={this.props.kioskId}
          displayStudentQueue={this.props.displayStudentQueue}
          isStudent={this.props.isStudent}
          isServiceMember={this.props.isServiceMember}
          kioskType={this.props.kioskType}
        />
        <div className={css['service-container']}>
          <div className={css['left-side']}>
            <ResponsiveView showIn="mobile">
              <WaitingRoom
                kioskQueueSize={this.props.kioskQueueSize}
                kioskMaxWaitTime={this.props.kioskMaxWaitTime}
                kioskId={this.props.kioskId}
                kioskType={this.props.kioskType}
                displayStudentQueue={this.props.displayStudentQueue}
                isServiceMember={this.props.isServiceMember}
              />
            </ResponsiveView>
            {this.appointmentSchedulingLink}
            {this.virtualCheckInLink}
            {this.hoursSection}
            {this.contactSection}
            {this.locationSection}
            {this.linksSection}
          </div>
          <div className={css['right-side']}>
            <ProvidersContainer id={this.props.id} onFailure={this.props.onFailure} />
            {this.overviewSection}
          </div>
        </div>
      </div>
    );
  }
}

ServiceProfile.propTypes = {
  id: SFEntityID.isRequired,
  uuid: PropTypes.string.isRequired,
  name: PropTypes.string,
  serviceDisplayName: PropTypes.string,
  location: PropTypes.string,
  phoneNumber: PropTypes.string,
  email: PropTypes.string,
  url: PropTypes.string,
  hoursOfOperation: PropTypes.string,
  serviceDescription: PropTypes.string,
  kioskQueueSize: PropTypes.number,
  kioskMaxWaitTime: PropTypes.number,
  onlineSchedulingAvailable: PropTypes.bool,
  serviceCalendarEnabled: PropTypes.bool,
  kioskId: PropTypes.number,
  displayStudentQueue: PropTypes.bool,
  displaySmartThinkingLink: PropTypes.bool,
  isServiceMember: PropTypes.bool.isRequired,
  isStudent: PropTypes.bool.isRequired,
  isStaff: PropTypes.bool.isRequired,
  canSeeStudentServices: PropTypes.bool,
  canMakeAppointment: PropTypes.bool,
  hasConnectLicense: PropTypes.bool,
  onFailure: PropTypes.func.isRequired,
  kioskType: PropTypes.string,
  virtualCheckInEnabled: PropTypes.bool,
  kioskUuid: PropTypes.string,
  checkedIntoService: PropTypes.bool,
  virtualCheckInOpen: PropTypes.bool,
  virtualKioskSchedule: PropTypes.arrayOf(SFVirtualCheckInDaySchedule)
};
